'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import PortalIdParser from 'PortalIdParser';
import I18n from 'I18n';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDuration from 'I18n/components/FormattedDuration';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import { isFailed, isFulfilled } from 'ecosystem-marketplace-core/utils/requestState';
import ThemeDetailsResponse from 'ecosystem-marketplace-core/records/ThemeDetailsResponse';
import * as AssetUninstallableReason from '../../constants/AssetUninstallableReason';
import { useAssetInstallability } from '../../hooks/useAssetInstallability';
import { getIsPublicApp } from '../../selectors/app';
import { getThemeEventSelectorId } from '../../utils/hsAnalytics';
import { getAssetShortlink, getHubSpotFreeLink, getDownloadedAssetsUpdateUrl, getDownloadedAssetsUrl, getCmsHubPricingUrl, getMarketingHubPricingUrl } from '../../utils/urls';
import CurrencyText from './CurrencyText';
import ThemeDetailsUsageTrackerContext from '../../context/ThemeDetailsUsageTrackerContext';
import ThemeOrderSummaryModal from './ThemeOrderSummaryModal';
import ThemeSignInButton from './ThemeSignInButton';
const ONE_MINUTE = 60 * 1000;

const ThemeDownloadButton = (_ref) => {
  let {
    disabled,
    section,
    themeDetails,
    themeScopes
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["disabled", "section", "themeDetails", "themeScopes"]);

  const {
    pathname
  } = useLocation();
  const {
    offering: {
      slug
    },
    offeringId,
    pricing
  } = themeDetails;
  const {
    trackInteraction
  } = useContext(ThemeDetailsUsageTrackerContext);
  const {
    assetUninstallableReason,
    installability,
    missingPortalScopes,
    requestState: assetInstallabilityRequestState
  } = useAssetInstallability({
    offeringId,
    planType: pricing.planType,
    requiredScopes: themeScopes
  });
  const isPublicApp = useSelector(getIsPublicApp);
  const {
    installed,
    canUpdate,
    pendingPaymentExpiry
  } = installability || {};
  const isUpdateAvailable = installed && canUpdate;
  const isFree = pricing.planType === PlanType.FREE;
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const handleClick = useCallback(() => {
    setOrderSummaryOpen(true);
    trackInteraction('click install button', {
      section
    });
  }, [section, trackInteraction]);
  const computedButtonProps = useMemo(() => {
    if (isUpdateAvailable) {
      return {
        href: getDownloadedAssetsUpdateUrl(offeringId)
      };
    }

    return {};
  }, [isUpdateAvailable, offeringId]);
  const {
    hasAccess = true,
    tooltip
  } = useMemo(() => {
    if (!isFulfilled(assetInstallabilityRequestState)) {
      return {
        hasAccess: false
      };
    }

    if (isFailed(assetInstallabilityRequestState)) {
      return {
        hasAccess: false,
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.installabilityRequestFailed"
        })
      };
    }

    if (isUpdateAvailable) {
      return {
        hasAccess: true,
        tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.updateAvailable_jsx",
          elements: {
            Link: UILink
          },
          options: {
            downloadsLink: {
              href: getDownloadedAssetsUpdateUrl(offeringId)
            }
          }
        })
      };
    }

    switch (assetUninstallableReason) {
      case AssetUninstallableReason.FORBIDDEN_ADMIN_PREVIEW:
      case AssetUninstallableReason.FORBIDDEN_PUBLIC:
      case AssetUninstallableReason.ALREADY_INSTALLED:
        return {
          hasAccess: false
        };

      case AssetUninstallableReason.PROVIDER_OFFERING:
        return {
          hasAccess: false,
          tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.providerAsset_jsx",
            elements: {
              Link: UILink
            },
            options: {
              anotherAccountLink: getAssetShortlink(pathname),
              hubspotFreeLink: getHubSpotFreeLink({
                hubs_content: window.location.href
              })
            }
          })
        };

      case AssetUninstallableReason.EXCEEDS_ASSET_LIMIT:
        return {
          hasAccess: false,
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.exceedsAssetLimit"
          })
        };

      case AssetUninstallableReason.PURCHASED_NOT_INSTALLED:
        return {
          hasAccess: false,
          tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.purchasedNotInstalled_jsx",
            elements: {
              Link: UILink
            },
            options: {
              downloadsLink: {
                href: getDownloadedAssetsUrl()
              }
            }
          })
        };

      case AssetUninstallableReason.PENDING_PAYMENT:
        {
          const duration = /*#__PURE__*/_jsx(FormattedDuration, {
            from: new Date().valueOf(),
            to: pendingPaymentExpiry + ONE_MINUTE
          });

          return {
            hasAccess: false,
            tooltip: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.paymentPendingExpiry",
              options: {
                duration
              }
            })
          };
        }

      case AssetUninstallableReason.PENDING_INSTALL:
        return {
          hasAccess: false,
          tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.pendingInstall_jsx",
            elements: {
              Link: UILink
            },
            options: {
              downloadsLink: {
                href: getDownloadedAssetsUrl()
              }
            }
          })
        };

      case AssetUninstallableReason.PENDING_UNINSTALL:
        return {
          hasAccess: false,
          tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.pendingUninstall_jsx",
            elements: {
              Link: UILink
            },
            options: {
              downloadsLink: {
                href: getDownloadedAssetsUrl()
              }
            }
          })
        };

      case AssetUninstallableReason.MISSING_REQUIRED_SUBSCRIPTION:
        return {
          hasAccess: false,
          tooltip: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.missingRequiredSubscription_jsx",
            elements: {
              Link: UILink
            },
            options: {
              cmsHubLink: {
                external: true,
                href: getCmsHubPricingUrl()
              },
              marketingHubLink: {
                external: true,
                href: getMarketingHubPricingUrl()
              }
            }
          })
        };

      case AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS:
        return {
          hasAccess: false,
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.marketplaceAssetAccess"
          })
        };

      default:
        break;
    }

    if (themeScopes == null && !isFree) {
      return {
        hasAccess: false
      };
    }

    if (assetUninstallableReason === AssetUninstallableReason.MISSING_HUB_SCOPES) {
      const scopeFeatures = missingPortalScopes.reduce((final, scope) => {
        const scopeFeatureI18nKey = `marketplace-assets-ui.scopeFeatures.${scope}`;

        if (isValidI18nKey(scopeFeatureI18nKey)) {
          final.push(I18n.text(scopeFeatureI18nKey));
        }

        return final;
      }, []);
      const missingScopesTooltip = scopeFeatures.length ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.missingPortalScopes",
        options: {
          count: missingPortalScopes.length,
          scopeFeatures: I18n.formatList(scopeFeatures)
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.missingOtherPortalScopes"
      });
      return {
        hasAccess: !!isFree,
        tooltip: missingScopesTooltip
      };
    }

    return {
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.tooltip.portalLicense",
        options: {
          portalId: PortalIdParser.get()
        }
      })
    };
  }, [assetInstallabilityRequestState, assetUninstallableReason, isFree, isUpdateAvailable, missingPortalScopes, offeringId, pathname, pendingPaymentExpiry, themeScopes]);
  const renderedButtonText = useMemo(() => {
    if (isUpdateAvailable) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.updateAvailable"
      });
    }

    if (installed) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.installed"
      });
    }

    if (isFree) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.downloadButton.free"
      });
    }

    return isMobileView ? /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "marketplace-assets-ui.themes.details.downloadButton.paidWithPrice",
      options: {
        price: /*#__PURE__*/_jsx(CurrencyText, {
          cents: pricing.cents
        })
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.themes.details.downloadButton.paid"
    });
  }, [isUpdateAvailable, installed, isFree, isMobileView, pricing]);
  return isPublicApp ? /*#__PURE__*/_jsx(ThemeSignInButton, Object.assign({}, rest, {
    section: section,
    slug: slug
  })) : /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UITooltip, {
      disabled: !tooltip,
      title: tooltip,
      children: /*#__PURE__*/_jsx(UIButton, Object.assign({}, rest, {}, computedButtonProps, {
        "data-hs-analytics-id": getThemeEventSelectorId('DOWNLOAD'),
        disabled: disabled || !hasAccess,
        onClick: handleClick,
        use: "primary",
        children: renderedButtonText
      }))
    }), orderSummaryOpen && /*#__PURE__*/_jsx(ThemeOrderSummaryModal, {
      onClose: () => setOrderSummaryOpen(false),
      themeDetails: themeDetails
    })]
  });
};

ThemeDownloadButton.propTypes = {
  disabled: PropTypes.bool,
  section: PropTypes.string,
  themeDetails: PropTypes.instanceOf(ThemeDetailsResponse).isRequired,
  themeScopes: PropTypes.arrayOf(PropTypes.string)
};
export default ThemeDownloadButton;